// Drift Auditor -- holdings + simulation-parameter input form.
//
// Pure UI (mirrors the Portfolio Diversification Checker's PortfolioForm): owns
// only local form state and delegates the network call upward via `onSubmit`.

import React, { useEffect, useMemo, useState } from 'react';

import { useDebounce } from '../../../shared/hooks/useDebounce';
import { SymbolAutocomplete } from '../../../shared/ui/SymbolAutocomplete';
import { useDriftCoverage } from '../api';
import type { CoverageResponse, DriftAuditRequest, HoldingRow } from '../types';

interface DriftAuditorFormProps {
    onSubmit: (data: DriftAuditRequest) => void;
    isPending: boolean;
    error: Error | null;
}

const MIN_HOLDINGS = 2;
const MAX_HOLDINGS = 30;
const TICKER_RE = /^[A-Z0-9^][A-Z0-9.\-=]{0,15}$/;

const PRESETS: { label: string; holdings: [string, number][] }[] = [
    { label: 'Classic 60/40', holdings: [['SPY', 60], ['AGG', 40]] },
    {
        label: 'Tech-Heavy',
        holdings: [['NVDA', 30], ['MSFT', 25], ['GOOGL', 20], ['AAPL', 15], ['AVGO', 10]],
    },
    {
        label: 'All-Weather',
        holdings: [['SPY', 30], ['TLT', 40], ['IEF', 15], ['GLD', 7.5], ['DBC', 7.5]],
    },
    {
        label: 'Global UCITS',
        holdings: [['VWCE.DE', 50], ['SXR8.DE', 30], ['AGGH', 20]],
    },
];

const STANDARD_LOOKBACK_OPTIONS = [1, 2, 3, 5, 10, 15, 20];

interface LookbackOption {
    value: number;
    label: string;
}

// Build the lookback dropdown from the ACTUAL available history: drop year
// options the price store cannot satisfy and always offer the full available
// window so re-running with a different lookback genuinely changes the result.
export const buildLookbackOptions = (coverage?: CoverageResponse | null): LookbackOption[] => {
    const yearLabel = (y: number): string => `${y} ${y === 1 ? 'Year' : 'Years'}`;
    const maxY = coverage?.max_lookback_years ?? 0;

    // No coverage info yet (still loading / tickers invalid) -> show the full
    // standard list rather than blocking the UI.
    if (!coverage || maxY <= 0) {
        return STANDARD_LOOKBACK_OPTIONS.map((y) => ({ value: y, label: yearLabel(y) }));
    }

    const opts: LookbackOption[] = STANDARD_LOOKBACK_OPTIONS.filter((y) => y <= maxY).map((y) => ({
        value: y,
        label: yearLabel(y),
    }));
    const topStd = opts.length ? opts[opts.length - 1].value : 0;

    // Add an explicit "max available" option when the real window isn't already
    // represented by a standard year (e.g. only ~6 months, or 12y of 15y tiers).
    if (opts.length === 0 || maxY - topStd >= 0.5) {
        const value = Math.max(1, Math.ceil(maxY));
        const label =
            maxY >= 1
                ? `${maxY.toFixed(1)} Years (max available)`
                : `~${Math.max(1, Math.round(maxY * 12))} Months (max available)`;
        if (!opts.some((o) => o.value === value)) {
            opts.push({ value, label });
        }
    }
    return opts;
};

let nextId = 1;
const toRows = (holdings: [string, number][]): HoldingRow[] =>
    holdings.map(([ticker, weight]) => ({ id: nextId++, ticker, weight }));

const inputClass =
    'w-full px-3.5 py-3 bg-slate-50 border border-slate-200 rounded-lg text-base text-slate-900 placeholder:text-slate-400 placeholder:normal-case [color-scheme:light] outline-none transition-colors focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40';

const TICKER_PLACEHOLDERS = ['e.g. SPY', 'e.g. AGG', 'e.g. VWCE.DE', 'e.g. BTCUSD', 'e.g. GLD'];

export const DriftAuditorForm: React.FC<DriftAuditorFormProps> = ({ onSubmit, isPending, error }) => {
    const [rows, setRows] = useState<HoldingRow[]>(() => toRows(PRESETS[0].holdings));
    const [initialCapital, setInitialCapital] = useState<number>(100_000);
    const [lookbackYears, setLookbackYears] = useState<number>(5);
    const [corridorPct, setCorridorPct] = useState<number>(5);
    const [txCostBps, setTxCostBps] = useState<number>(10);
    const [localError, setLocalError] = useState<string | null>(null);

    const totalWeight = useMemo(
        () => rows.reduce((sum, r) => sum + (Number.isFinite(r.weight) ? r.weight : 0), 0),
        [rows],
    );

    // Probe the price store for how much overlapping history these tickers have
    // so the Lookback dropdown only offers windows we can actually serve.
    const validTickers = useMemo(() => {
        const out: string[] = [];
        const seen = new Set<string>();
        for (const r of rows) {
            const t = r.ticker.trim().toUpperCase();
            if (t && TICKER_RE.test(t) && !seen.has(t)) {
                seen.add(t);
                out.push(t);
            }
        }
        return out;
    }, [rows]);
    const debouncedTickers = useDebounce(validTickers, 400);
    const { data: coverage } = useDriftCoverage(debouncedTickers);

    const lookbackOptions = useMemo(() => buildLookbackOptions(coverage), [coverage]);

    // Clamp the selection to a window the data can serve whenever the available
    // options change (e.g. after adding a recently-listed ticker).
    useEffect(() => {
        const values = lookbackOptions.map((o) => o.value);
        if (values.length && !values.includes(lookbackYears)) {
            setLookbackYears(values[values.length - 1]);
        }
    }, [lookbackOptions, lookbackYears]);

    const updateRow = (id: number, patch: Partial<HoldingRow>) =>
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const removeRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id));

    const addRow = () =>
        setRows((prev) =>
            prev.length >= MAX_HOLDINGS ? prev : [...prev, { id: nextId++, ticker: '', weight: 10 }],
        );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const seen = new Set<string>();
        const holdings: { ticker: string; weight: number }[] = [];
        for (const row of rows) {
            const ticker = row.ticker.trim().toUpperCase();
            if (!ticker) continue;
            if (!TICKER_RE.test(ticker)) {
                setLocalError(`"${row.ticker}" is not a valid ticker symbol.`);
                return;
            }
            if (!(row.weight > 0)) {
                setLocalError(`Weight for ${ticker} must be greater than 0.`);
                return;
            }
            if (seen.has(ticker)) {
                setLocalError(`Duplicate ticker ${ticker}. Combine its weight into one row.`);
                return;
            }
            seen.add(ticker);
            holdings.push({ ticker, weight: row.weight });
        }
        if (holdings.length < MIN_HOLDINGS || holdings.length > MAX_HOLDINGS) {
            setLocalError(`Please enter between ${MIN_HOLDINGS} and ${MAX_HOLDINGS} distinct holdings.`);
            return;
        }
        if (!(initialCapital >= 1000)) {
            setLocalError('Initial capital must be at least $1,000.');
            return;
        }
        // Re-normalise percentages -> fractions summing to exactly 1.0.
        const total = holdings.reduce((s, h) => s + h.weight, 0);
        setLocalError(null);
        onSubmit({
            holdings: holdings.map((h) => ({ ticker: h.ticker, target_weight: h.weight / total })),
            initial_capital: initialCapital,
            lookback_years: lookbackYears,
            corridor_threshold: corridorPct / 100,
            transaction_cost_bps: txCostBps,
        });
    };

    const shownError = localError ?? error?.message ?? null;
    const isBalanced = Math.abs(totalWeight - 100) < 0.01;

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-5"
        >
            <div>
                <p className="block text-sm font-semibold text-slate-700 mb-2">Quick-start examples</p>
                <div className="flex flex-wrap gap-2">
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.label}
                            type="button"
                            onClick={() => setRows(toRows(preset.holdings))}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full border border-slate-200 bg-slate-50 text-slate-700 hover:border-indigo-400 hover:text-indigo-700 transition-colors"
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your Target Allocation</label>
                <div className="flex gap-2 items-center px-1 mb-1.5">
                    <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Ticker / Symbol
                    </span>
                    <span className="w-28 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Target (%)
                    </span>
                    <span className="w-10 shrink-0" aria-hidden="true"></span>
                </div>
                <div className="space-y-2.5">
                    {rows.map((row, index) => (
                        <div key={row.id} className="flex gap-2 items-center">
                            <SymbolAutocomplete
                                value={row.ticker}
                                onChange={(next) => updateRow(row.id, { ticker: next })}
                                accent="indigo"
                                placeholder={TICKER_PLACEHOLDERS[index % TICKER_PLACEHOLDERS.length]}
                                ariaLabel={`Ticker for holding ${index + 1}`}
                                maxLength={16}
                                inputClassName={`${inputClass} font-semibold tracking-wide uppercase`}
                                listboxId={`drift-symbols-${row.id}`}
                            />
                            <div className="relative w-28 shrink-0">
                                <input
                                    type="number"
                                    value={Number.isFinite(row.weight) ? row.weight : ''}
                                    onChange={(e) => updateRow(row.id, { weight: Number(e.target.value) })}
                                    min="0"
                                    step="any"
                                    inputMode="decimal"
                                    aria-label={`Target weight in percent for holding ${index + 1}`}
                                    className={`${inputClass} pr-7 text-right`}
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-400">
                                    %
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeRow(row.id)}
                                disabled={rows.length <= MIN_HOLDINGS}
                                aria-label={`Remove ${row.ticker || `holding ${index + 1}`}`}
                                className="shrink-0 w-10 h-11 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-colors"
                            >
                                <i className="bx bx-x text-xl" aria-hidden="true"></i>
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center justify-between mt-3">
                    <button
                        type="button"
                        onClick={addRow}
                        disabled={rows.length >= MAX_HOLDINGS}
                        className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
                    >
                        + Add holding
                    </button>
                    <span
                        className={`text-xs font-bold px-2 py-1 rounded ${
                            isBalanced ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                        }`}
                    >
                        Total: {totalWeight.toFixed(1)}%
                    </span>
                </div>
                {!isBalanced && (
                    <p className="text-xs text-slate-500 mt-2">Weights are automatically rescaled to 100%.</p>
                )}
                <p className="text-xs text-slate-400 mt-2">
                    Stocks, ETFs and crypto supported, e.g. SPY, VWCE.DE, BTCUSD.
                </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Initial Capital</label>
                    <div className="relative">
                        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-slate-400">
                            $
                        </span>
                        <input
                            type="number"
                            value={Number.isFinite(initialCapital) ? initialCapital : ''}
                            onChange={(e) => setInitialCapital(Number(e.target.value))}
                            min="1000"
                            step="1000"
                            className={`${inputClass} pl-7`}
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lookback</label>
                    <select
                        value={lookbackYears}
                        onChange={(e) => setLookbackYears(Number(e.target.value))}
                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 [color-scheme:light] outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                        {lookbackOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {coverage && coverage.max_lookback_years > 0 && (
                        <p className="mt-1.5 text-xs text-slate-400">
                            {coverage.max_lookback_years >= 1
                                ? `~${coverage.max_lookback_years.toFixed(1)} years`
                                : `~${Math.max(1, Math.round(coverage.max_lookback_years * 12))} months`}{' '}
                            of shared history for these tickers.
                        </p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                        Rebalance Corridor
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            value={Number.isFinite(corridorPct) ? corridorPct : ''}
                            onChange={(e) => setCorridorPct(Number(e.target.value))}
                            min="1"
                            max="25"
                            step="1"
                            className={`${inputClass} pr-9 text-right`}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-400">
                            ±%
                        </span>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                        Transaction Cost
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            value={Number.isFinite(txCostBps) ? txCostBps : ''}
                            onChange={(e) => setTxCostBps(Number(e.target.value))}
                            min="0"
                            max="100"
                            step="1"
                            className={`${inputClass} pr-12 text-right`}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-400">
                            bps
                        </span>
                    </div>
                </div>
            </div>

            <button
                type="submit"
                disabled={isPending}
                className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-70 flex justify-center items-center"
            >
                {isPending ? 'Running Simulation...' : 'Audit My Portfolio'}
            </button>

            {shownError && (
                <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100 mt-2 font-medium">
                    {shownError}
                </div>
            )}
        </form>
    );
};
